import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import moment from 'moment';
import { db } from 'src/app/app.component';
import { valueToDate } from 'src/app/globals';
import { Organisation, OrgUser } from 'src/app/interfaces';
import { BulkDeleteHiddenPassengersDialogComponent } from './bulk-delete-dialog/bulk-delete-hidden-passengers-dialog.component';

@Component({
  selector: 'app-bulk-delete-hidden-passengers',
  templateUrl: './bulk-delete-hidden-passengers.component.html',
  styleUrl: './bulk-delete-hidden-passengers.component.scss',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatIcon,
    MatCheckbox,
    MatButton,
    MatProgressSpinner,
  ],
})
export class BulkDeleteHiddenPassengersComponent implements OnDestroy, OnInit {
  readonly organisationControl = new FormControl<Organisation | null>(null, Validators.required);
  readonly searchControl = new FormControl('');
  readonly searchTerm = signal('');
  readonly organisations = signal<Organisation[]>([]);
  readonly users = signal<(OrgUser & { id: string })[]>([]);
  readonly selectedIds = signal<string[]>([]);
  readonly loading = signal(true);
  readonly processing = signal(false);
  readonly errorMessage = signal('');
  private unsubscribeUsersListener: (() => void) | null = null;
  readonly visibleUsers = computed(() => {
    const search = this.searchTerm().trim().toLocaleLowerCase();
    return this.users().filter((user) => {
      const searchable =
        `${user.name ?? ''} ${user.email ?? ''} ${user.phone ?? user.phoneNumber ?? ''}`.toLocaleLowerCase();
      return !search || searchable.includes(search);
    });
  });
  readonly selectedUsers = computed(() => {
    const selected = new Set(this.selectedIds());
    return this.users().filter((user) => selected.has(user.id));
  });
  readonly allVisibleUsersSelected = computed(() => {
    const visible = this.visibleUsers();
    return visible.length > 0 && visible.every((user) => this.selectedIds().includes(user.id));
  });

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<BulkDeleteHiddenPassengersComponent>,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    this.searchControl.disable({ emitEvent: false });
    this.searchControl.valueChanges.subscribe((value) => this.searchTerm.set(value ?? ''));
    this.organisationControl.valueChanges.subscribe((organisation) => {
      void this.selectOrganisation(organisation);
    });
    await this.loadOrganisations();
  }

  ngOnDestroy(): void {
    this.unsubscribeUsersListener?.();
  }

  private async loadOrganisations(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, 'organisations'));
      const organisations = snapshot.docs
        .map((organisationDoc) => ({
          id: organisationDoc.id,
          ...(organisationDoc.data() as Organisation),
        }))
        .sort((first, second) => first.name.localeCompare(second.name));
      this.organisations.set(organisations);
    } catch (error) {
      console.error('Failed to load organisations:', error);
      this.errorMessage.set('De organisaties konden niet worden geladen.');
    } finally {
      this.loading.set(false);
    }
  }

  private async selectOrganisation(organisation: Organisation | null): Promise<void> {
    this.unsubscribeUsersListener?.();
    this.unsubscribeUsersListener = null;
    this.users.set([]);
    this.selectedIds.set([]);
    this.searchControl.setValue('');
    this.errorMessage.set('');
    if (!organisation?.id) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }

    this.searchControl.enable({ emitEvent: false });
    this.loading.set(true);
    const usersQuery = query(
      collection(db, `organisations/${organisation.id}/users`),
      where('hidden', '==', true),
    );
    try {
      this.unsubscribeUsersListener = onSnapshot(
        usersQuery,
        (usersSnapshot) => {
          if (this.organisationControl.value?.id !== organisation.id) {
            return;
          }
          const users = usersSnapshot.docs
            .map((userDoc) => ({ id: userDoc.id, ...(userDoc.data() as OrgUser) }))
            .filter(
              (user) =>
                user.passenger === true &&
                !user.roles?.length &&
                !user.superuser &&
                user.deleted !== true,
            )
            .sort((first, second) => (first.name ?? '').localeCompare(second.name ?? ''));
          const userIds = new Set(users.map((user) => user.id));
          this.users.set(users);
          this.selectedIds.update((selectedIds) =>
            selectedIds.filter((selectedId) => userIds.has(selectedId)),
          );
          this.loading.set(false);
        },
        (error) => {
          console.error('Failed to listen for hidden passengers:', error);
          this.errorMessage.set('De verborgen passagiers konden niet worden geladen.');
          this.loading.set(false);
        },
      );
    } catch (error) {
      console.error('Failed to listen for hidden passengers:', error);
      this.errorMessage.set('De verborgen passagiers konden niet worden geladen.');
      this.loading.set(false);
    }
  }

  toggleSelection(user: OrgUser & { id: string }, checked: boolean): void {
    const selected = new Set(this.selectedIds());
    if (checked) {
      selected.add(user.id);
    } else {
      selected.delete(user.id);
    }
    this.selectedIds.set([...selected]);
  }

  toggleVisibleSelection(checked: boolean): void {
    const selected = new Set(this.selectedIds());
    this.visibleUsers().forEach((user) => {
      if (checked) {
        selected.add(user.id);
      } else {
        selected.delete(user.id);
      }
    });
    this.selectedIds.set([...selected]);
  }

  requestDeletion(): void {
    const selected = this.selectedUsers();
    if (selected.length === 0) {
      return;
    }

    const confirmationUsers = selected.map((user) => ({
      id: user.id,
      name: user.name || 'Onbekend',
      email: user.email || '-',
      phone: user.phone || user.phoneNumber || '-',
      hiddenDate: this.formatDate(user.hiddenDate),
    }));
    this.dialog
      .open(BulkDeleteHiddenPassengersDialogComponent, {
        width: '750px',
        autoFocus: false,
        data: {
          mode: 'confirmation',
          users: confirmationUsers,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          void this.bulkDelete();
        }
      });
  }

  async bulkDelete(): Promise<void> {
    const organisationId = this.organisationControl.value?.id;
    const selected = this.selectedUsers();
    if (!organisationId || selected.length === 0 || this.processing()) {
      return;
    }

    this.processing.set(true);
    this.errorMessage.set('');
    const queued: (OrgUser & { id: string })[] = [];
    const skipped: (OrgUser & { id: string })[] = [];
    const failed: (OrgUser & { id: string })[] = [];
    const authUser = getAuth().currentUser;

    await Promise.all(
      selected.map(async (user) => {
        try {
          const userSnapshot = await getDoc(
            doc(db, `organisations/${organisationId}/users/${user.id}`),
          );
          const currentUser = userSnapshot.data() as OrgUser | undefined;
          if (
            !userSnapshot.exists() ||
            !currentUser?.hidden ||
            currentUser.passenger !== true ||
            currentUser.superuser ||
            currentUser.roles?.length
          ) {
            skipped.push(user);
            return;
          }
          const queueRef = doc(db, `organisations/${organisationId}/deleteUsersQueue/${user.id}`);
          if ((await getDoc(queueRef)).exists()) {
            skipped.push(user);
            return;
          }
          await setDoc(queueRef, {
            email: user.email ?? '',
            phone: user.phone ?? user.phoneNumber ?? '',
            date: new Date(),
            status: 'todo',
            userData: user,
            adminEmail: authUser?.email ?? '',
          });
          queued.push(user);
        } catch (error) {
          console.error(`Failed to queue ${user.id}:`, error);
          failed.push(user);
        }
      }),
    );

    try {
      await addDoc(collection(db, `eventLogs/${organisationId}/deletedUser`), {
        bulkActionType: 'bulkDeleteHiddenPassengers',
        date: new Date(),
        organisationId,
        doneByUser: authUser?.email ?? '',
        doneByUid: authUser?.uid ?? '',
        userIds: selected.map((user) => user.id),
        emails: selected.map((user) => user.email ?? ''),
        queued: queued.map((user) => user.id),
        skipped: skipped.map((user) => user.id),
        failed: failed.map((user) => user.id),
      });
    } catch (error) {
      console.error('Failed to write bulk deletion audit log:', error);
      this.errorMessage.set(
        'De wachtrij is bijgewerkt, maar de auditlog kon niet worden opgeslagen.',
      );
    }

    this.processing.set(false);
    if (!failed.length) {
      this.snackBar.open('De geselecteerde passagiers worden binnenkort verwijderd.', 'Sluiten', {
        duration: 5000,
      });
      this.dialogRef.close();
      return;
    }

    this.dialog.open(BulkDeleteHiddenPassengersDialogComponent, {
      width: '750px',
      autoFocus: false,
      data: {
        mode: 'result',
        queued,
        skipped,
        failed,
      },
    });
  }

  formatDate(value: any): string {
    const date = valueToDate(value);
    if (!date) {
      return 'Onbekend';
    }
    return moment(date).format('DD-MM-YYYY');
  }
}
